import { BusinessRounded, QueryBuilderRounded } from '@mui/icons-material'
import { Box, Card, CardActions, CardContent, CardMedia, Typography } from '@mui/material'
import { memo } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch } from 'react-redux'
import { useNavigate } from 'react-router'
import { setFavorite, unsetFavorite } from '../../../../../redux/actions/favorites'
import LinearProgress from '../../../../backoffice/courses/ui/components/LinearProgress'
import CustomChip from '../../../../shared/components/CustomChip'
import TooltipCards from '../../../../shared/components/TooltipCards'
import FavoriteButton from '../../../../shared/components/buttons/FavoriteButton'
import Visibility from '../../../../shared/components/frontoffice/Visibility'
import ShareIcon from '../../../../shared/components/icons/ShareIcon'
import useGeneralSummaryReport from '../../../../shared/hooks/useGeneralSummaryReport'
import useScreenWidth from '../../../../shared/hooks/useScreenWidth'
import HiddenDesktop from '../../../../shared/styles/HiddenDesktop'
import HiddenMobile from '../../../../shared/styles/HiddenMobile'

type Course = {
  id: number
  brand_id: number
  brandName: string
  favoriteId: number
  title: string
  slug: string
  description: string
  duration: number
  image_url: string
  status: number
  visibility: number
}
type CourseBlockProps = {
  course: Course
  progress?: { course_id: number; value: number }
}

function CourseBlock({ course, progress }: CourseBlockProps) {
  const { t } = useTranslation(['global'])
  const isMobile = useScreenWidth(700)
  const navigate = useNavigate()

  const dispatch = useDispatch()
  const { handleClickGeneralSummaryReport } = useGeneralSummaryReport(3)

  const showEraserChip = (status: any) => {
    if (status === 2) {
      return <CustomChip type={2} label={t('draft')} color={'grey'} />
    }
  }

  const handleLink = () => {
    handleClickGeneralSummaryReport(course.id)
    navigate(`/cursos/${course.brand_id}/${course.slug}`)
  }

  const handleFavoriteClick = (course_id: number) => (state: boolean) => {
    handleClickGeneralSummaryReport(course_id)
    if (state) {
      dispatch(
        setFavorite({
          id: course_id,
          state,
          type: 'course',
          favoritefield: 'courses'
        })
      )
    } else {
      dispatch(
        unsetFavorite({
          id: course_id,
          state,
          type: 'course',
          favoritefield: 'courses'
        })
      )
    }
  }

  return (
    <>
      <Card
        sx={[
          {
            border: 'none',
            position: 'relative',
            boxShadow: '4px 6px 20px rgb(0 0 0 / 10%)',
            borderRadius: '8px',
            '& .MuiCardActionArea-focusHighlight': {
              backgroundColor: 'grey.800'
            },
            boxSizing: { lg: 'border-box', xl: 'border-box' }
          },
          course.visibility === 1 && { boxShadow: '0px 4px 32px 0px rgba(230, 185, 128, 0.3) !important' }
        ]}
      >
        <CardMedia
          sx={theme => ({
            [theme.breakpoints.up('md')]: {
              height: '163px'
            },
            [theme.breakpoints.down('sm')]: {
              height: '136px'
            },
            position: 'relative',
            cursor: 'pointer'
          })}
          onClick={() => handleLink()}
          image={`${course.image_url}?${Date.now()}`}
          title={course.title}
        >
          {course.visibility === 1 && (
            <Box
              sx={theme => ({
                position: 'absolute',
                right: '0',
                top: 8,
                width: 64,
                borderRadius: '4px 0px 0px 4px',
                padding: '0px 4px',
                background: 'linear-gradient(81.59deg, #A6813C -6.65%, #F2E3B3 50.89%, #BFA75E 110.5%)',
                '& h6': {
                  fontWeight: 500
                },
                [theme.breakpoints.down('sm')]: {
                  top: 0,
                  padding: 0,
                  '& p': {
                    fontWeight: 500
                  }
                }
              })}
              textAlign="center"
            >
              <Typography variant={isMobile ? 'body1' : 'subtitle1'}>VIP</Typography>
            </Box>
          )}
        </CardMedia>

        <CardContent sx={{ p: 1.5, pb: 0 }}>
          <Box style={{ cursor: 'pointer' }} onClick={() => handleLink()}>
            <LinearProgress progress={Number(progress?.value) ?? 0} vip={course.visibility === 1} />
            <TooltipCards titleCard={course.title} classNameCustom="courseTitle" variantCustom="subtitle1" />
            <Box minHeight="19.5px">
              {course.duration !== 0 && (
                <Box display="flex" alignItems="center" gap={4}>
                  <QueryBuilderRounded fontSize="small" htmlColor="#8F99A3" />
                  <Typography
                    sx={{
                      color: 'grey.400',
                      fontSize: '13px',
                      fontWeight: 500,
                      height: 18
                    }}
                    variant="body1"
                  >
                    {t('duration')}: {`${course.duration} min`}
                  </Typography>
                </Box>
              )}
            </Box>
          </Box>
        </CardContent>

        <CardActions style={{ padding: 0, justifyContent: 'space-between' }} disableSpacing>
          <Box display="flex" pl={isMobile ? '6px' : 1}>
            <FavoriteButton type="icon" handleClick={handleFavoriteClick(course.id)} favoriteState={Boolean(course.favoriteId)} />
            <HiddenMobile>
              {showEraserChip(course.status)}
              <ShareIcon
                meta={true}
                visibility={course.visibility}
                title={course.title}
                type="course"
                description={course.description}
                link={`${window.location.origin}/cursos/${course.brand_id}/${course.slug}${window.location.search}`}
                //multimedia={`${course.image_url}?${new Date().getTime()}`}
                multimedia={`${course.image_url}?${Date.now()}`}
                buttonText={t('share')}
                foreignId={course.id}
              />
            </HiddenMobile>
            <HiddenDesktop>
              <ShareIcon
                visibility={course.visibility}
                title={course.title}
                description={course.description}
                link={`${window.location.origin}/cursos/${course.brand_id}/${course.slug}${window.location.search}`}
                type="course"
                // multimedia={`${course.image_url}?${new Date().getTime()}`}
                multimedia={`${course.image_url}?${Date.now()}`}
                foreignId={course.id}
              />
            </HiddenDesktop>
          </Box>
          <Box
            width="max-width"
            height="48px"
            display="flex"
            alignItems="flex-end"
            flexDirection="column"
            pr={isMobile ? '6px' : 1}
            onClick={() => handleLink()}
            style={{ cursor: 'pointer' }}
          >
            <Box>
              <Visibility variant={isMobile && 5} type={course.visibility} />
            </Box>
            <HiddenDesktop>
              <Typography variant="caption" color="textSecondary">
                • {course.brandName}
              </Typography>
            </HiddenDesktop>

            <HiddenMobile display="flex" alignItems="center" gap={1}>
              <BusinessRounded fontSize="small" htmlColor="#8F99A3" />
              <Typography variant="body2" color="textSecondary">
                {course.brandName}
              </Typography>
            </HiddenMobile>
          </Box>
        </CardActions>
      </Card>
    </>
  )
}

export default memo(CourseBlock)
